import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, isStaff, isAdmin } from "@/context/AuthContext";
import BrandMark from "@/components/ventrix/Logo";
import {
  LayoutDashboard, Package, Plus, FileText, BookUser, Bell,
  Settings, LogOut, Shield, Users, Layers, ClipboardList, BarChart3,
  ChevronRight, Truck, LayoutGrid, MapPin, CreditCard, Wallet, Menu, X,
  Mail, Receipt, Building2, Calculator
} from "lucide-react";

const CUSTOMER_NAV = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/app/shipments", icon: Package, label: "Shipments" },
  { to: "/app/quote", icon: Calculator, label: "Request a quote" },
  { to: "/app/invoices", icon: FileText, label: "Invoices" },
  { to: "/app/address-book", icon: BookUser, label: "Address book" },
];

const OPS_NAV = [
  { to: "/ops", end: true, icon: LayoutDashboard, label: "Console" },
  { to: "/ops/shipments", icon: Package, label: "All shipments" },
  { to: "/ops/book", icon: Plus, label: "New booking" },
  { to: "/ops/rates", icon: Layers, label: "Rate cards" },
  { to: "/ops/carriers", icon: Truck, label: "Carriers" },
  { to: "/ops/quote-requests", icon: FileText, label: "Quote requests" },
  { to: "/ops/email-review", icon: Mail, label: "Email review" },
  { to: "/ops/invoices", icon: Receipt, label: "Invoices" },
  // Verifying payments is a finance/admin task; hidden from pure-ops below.
  { to: "/ops/payments", icon: Wallet, label: "Verify payments" },
];

// Dedicated admin portal (section="admin") at /backoffice, reached from Ops.
const ADMIN_NAV = [
  { to: "/backoffice", end: true, icon: LayoutDashboard, label: "Dashboard" },
  { to: "/backoffice/users", icon: Users, label: "Users & roles" },
  { to: "/backoffice/audit", icon: ClipboardList, label: "Audit log" },
  { to: "/backoffice/reports", icon: BarChart3, label: "Reports" },
  { to: "/backoffice/payments", icon: Wallet, label: "Verify payments" },
  { to: "/backoffice/quote-requests", icon: FileText, label: "Quote requests" },
  { to: "/backoffice/email-review", icon: Mail, label: "Email review" },
  { to: "/backoffice/invoices", icon: Receipt, label: "Invoices" },
  { to: "/backoffice/services", icon: LayoutGrid, label: "Services" },
];

// super_admin-only runtime settings, inside the admin portal.
const ADMIN_SETTINGS_NAV = [
  { to: "/backoffice/settings/company", icon: Building2, label: "Company & invoices" },
  { to: "/backoffice/settings/payments", icon: CreditCard, label: "Payments" },
  { to: "/backoffice/settings/notifications", icon: Bell, label: "Notifications" },
  { to: "/backoffice/settings/maps", icon: MapPin, label: "Maps" },
];

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <BrandMark size={30} />
      <div className="leading-tight">
        <div className="font-black text-[15px]">VENTRIX</div>
        <div className="text-[9px] tracking-[0.14em] text-[#A9C0E0] font-semibold">GLOBAL FREIGHTS LTD</div>
      </div>
    </Link>
  );
}

// Shared sidebar contents for both the desktop rail and the mobile drawer.
function SidebarBody({ section, user, sectionLabel, navItems, onLogout, onNavigate, idPrefix = "sidebar" }) {
  const admin = isAdmin(user?.role);
  const switchClass = "block px-3 py-2 rounded-sm text-sm bg-[#d41f26]/90 hover:bg-[#d41f26] font-semibold";
  return (
    <>
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        <div className="px-2 text-[10px] tracking-[0.2em] font-bold text-[#A9C0E0] uppercase mb-2">{sectionLabel}</div>
        {navItems.map((it) => <SidebarLink key={it.to} {...it} onClick={onNavigate} idPrefix={idPrefix} />)}
        {section === "admin" && user?.role === "super_admin" && (
          <>
            <div className="px-2 mt-6 text-[10px] tracking-[0.2em] font-bold text-[#A9C0E0] uppercase mb-2">Settings</div>
            {ADMIN_SETTINGS_NAV.map((it) => <SidebarLink key={it.to} {...it} onClick={onNavigate} idPrefix={idPrefix} />)}
          </>
        )}
        {section === "ops" && admin && (
          <div className="pt-6"><Link to="/backoffice" onClick={onNavigate} className={switchClass} data-testid="switch-to-admin">Open Admin portal</Link></div>
        )}
        {section === "admin" && (
          <div className="pt-6"><Link to="/ops" onClick={onNavigate} className={switchClass} data-testid="switch-to-ops">Open Ops console</Link></div>
        )}
        {isStaff(user?.role) && section === "customer" && (
          <div className="pt-6"><Link to="/ops" onClick={onNavigate} className={switchClass} data-testid="switch-to-ops">Open Ops console</Link></div>
        )}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center text-xs font-bold">
            {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.name || user?.email}</div>
            <div className="text-[10px] text-[#A9C0E0] uppercase tracking-widest">{user?.role?.replace("_", " ")}</div>
          </div>
        </div>
        <button onClick={onLogout} data-testid={`${idPrefix}-logout`} className="w-full flex items-center gap-2 text-sm text-white/80 hover:text-white px-2 py-1.5 rounded-sm hover:bg-white/10 transition-colors">
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );
}

export default function PortalLayout({ children, section = "customer" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  let navItems = section === "ops" ? OPS_NAV : section === "admin" ? ADMIN_NAV : CUSTOMER_NAV;
  // Only finance/admin/super_admin can verify payments — don't show the link to pure ops.
  if (section === "ops" && user?.role === "ops") {
    navItems = navItems.filter((n) => n.to !== "/ops/payments");
  }
  const sectionLabel = section === "ops" ? "Operations" : section === "admin" ? "Administration" : "My account";

  const doLogout = async () => { await logout(); nav("/", { replace: true }); };
  const closeMobile = () => setMobileOpen(false);

  const crumbs = loc.pathname.split("/").filter(Boolean);

  return (
    <div className="min-h-screen flex bg-[#F4FEFF]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col bg-[#0E2F76] text-white sticky top-0 h-screen">
        <div className="px-5 h-16 border-b border-white/10 flex items-center"><Brand /></div>
        <SidebarBody section={section} user={user} sectionLabel={sectionLabel} navItems={navItems} onLogout={doLogout} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" data-testid="mobile-nav">
          <div className="absolute inset-0 bg-black/40" onClick={closeMobile} />
          <aside className="absolute left-0 top-0 h-full w-64 max-w-[85%] flex flex-col bg-[#0E2F76] text-white shadow-2xl">
            <div className="px-5 h-16 border-b border-white/10 flex items-center justify-between">
              <Brand />
              <button onClick={closeMobile} aria-label="Close menu" data-testid="mobile-nav-close" className="p-2 -mr-1 text-white/80 hover:text-white"><X size={20} /></button>
            </div>
            <SidebarBody section={section} user={user} sectionLabel={sectionLabel} navItems={navItems} onLogout={doLogout} onNavigate={closeMobile} idPrefix="m-sidebar" />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-[#A9C0E0]/50 flex items-center justify-between px-4 md:px-6 sticky top-0 z-40">
          <div className="flex items-center gap-2 min-w-0">
            <button className="lg:hidden p-2 -ml-1 text-[#0E2F76]" onClick={() => setMobileOpen(true)} data-testid="mobile-nav-toggle" aria-label="Open menu"><Menu size={20} /></button>
            <nav className="hidden sm:flex items-center gap-1 text-xs min-w-0">
              {crumbs.map((c, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight size={12} className="text-[#A9C0E0]" />}
                  <span className={i === crumbs.length - 1 ? "font-semibold text-[#0E2F76]" : "text-[#475569]"}>
                    {c.replace(/-/g, " ")}
                  </span>
                </span>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {user?.mfa_enabled ? (
              <span className="hidden md:inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-bold text-[#166534]"><Shield size={12}/> MFA on</span>
            ) : (
              <Link to="/app/security" className="hidden md:inline-flex items-center gap-1.5 text-[10px] tracking-widest uppercase font-bold text-[#B45309] hover:text-[#d41f26]"><Shield size={12}/> Enable MFA</Link>
            )}
            <Link to="/app/security" className="p-2 hover:bg-[#F4FEFF] rounded-sm" data-testid="header-settings" aria-label="Settings"><Settings size={16} /></Link>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}

function SidebarLink({ to, end, icon: Icon, label, onClick, idPrefix = "sidebar" }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={`${idPrefix}-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
          isActive ? "bg-white text-[#0E2F76]" : "text-white/85 hover:bg-white/10"
        }`
      }
    >
      <Icon size={16} strokeWidth={2.2} />
      <span>{label}</span>
    </NavLink>
  );
}
